// src/components/FabricanteDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import {
  StyledMainContainer,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledTableContainer,
  StyledTableHeaderCell,
  StyledNoUsersCell,
  StyledActionsBar
} from './StyleComponents';

import FabricanteDetail from './FabricanteDetail';
import Header from './Header';
import api from './api';

function FabricanteDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFabricanteDetail, setOpenFabricanteDetail] = useState(false);
  const [selectedFabricante, setSelectedFabricante] = useState(null);
  const [fabricantes, setFabricantes] = useState([]);
  const [plantas, setPlantas] = useState([]);

  const [fabPltMap, setFabPltMap] = useState({});
  const [plantasIdToNameMap, setPlantasIdToNameMap] = useState({});


  const fetchFabricantes = () => {
    api.get('/fabricante/?skip=0&limit=100')
      .then((response) => {
        setFabricantes(response.data);
      })
      .catch((error) => {
        console.error('Error al obtener fabricantes:', error);
      });
  };

  const fetchPlantas = () => {
    api.get('/plantas/?skip=0&limit=10000')
      .then((response) => {
        setPlantas(response.data);
        // Construir el mapa de ID de planta a nombre
        const idToNameMap = response.data.reduce((acc, planta) => {
          acc[planta.id] = planta.nombre;
          return acc;
        }, {});
        setPlantasIdToNameMap(idToNameMap);

        // Construir el mapa de fabricanteId a array de plantas
        const newFabricantePlantasMap = response.data.reduce((acc, planta) => {
          if (planta.fabricante_id) { // Asume que la planta tiene un campo 'fabricante_id'
            if (!acc[planta.fabricante_id]) {
              acc[planta.fabricante_id] = [];
            }
            acc[planta.fabricante_id].push(planta); // Almacena el objeto completo de la planta
          }
          return acc;
        }, {});
        setFabPltMap(newFabricantePlantasMap);

      })
      .catch((error) => {
        console.error('Error al obtener plantas:', error);
      });

  };

  useEffect(() => {
    fetchFabricantes();
    fetchPlantas();
  }, []);

  const filteredFabricantes = fabricantes.filter(fabricante => {
    const term = searchTerm.toLowerCase();
    const statusText = fabricante.is_active ? 'activo' : 'inactivo';
    // Obtener las plantas asociadas a este fabricante y sus nombres
    const associatedPlants = fabPltMap[fabricante.id] || [];
    const plantNames = associatedPlants.map(planta => planta.nombre?.toLowerCase()).join(' '); // Unir nombres de plantas en una sola cadena

    return (
      (fabricante.nombre?.toLowerCase() || '').includes(term) ||
      (fabricante.tecnologia?.toLowerCase() || '').includes(term) ||
      (statusText.includes(term)) ||
      plantNames.includes(term) 
    );
  });

  const handleCreateFabricanteClick = () => {
    setSelectedFabricante(null);
    setOpenFabricanteDetail(true);
  };

  const handleEditClick = (fabricante) => {
    /*
    const assignedPlants = plantas.map(p => ({
      nombre_planta: p.nombre

    }))*/
    const assignedPlants = fabPltMap[fabricante.id] || [];

    setSelectedFabricante(fabricante);
    setOpenFabricanteDetail(true);
  };

  const handleCloseFabricanteDetail = () => {
    setOpenFabricanteDetail(false);
    setSelectedFabricante(null);
    fetchFabricantes();
    fetchPlantas(); 
  };

  const handleSaveFabricante = (updatedFabricanteData) => {
    if (updatedFabricanteData.id) {
      // Actualización: reemplaza en lista
      setFabricantes(prev =>
        prev.map(f =>
          f.id === updatedFabricanteData.id ? { ...f, ...updatedFabricanteData } : f
        )
      );
    } else {
      // Creación: recarga lista completa
      fetchFabricantes();
    }
    handleCloseFabricanteDetail();
  };

  return (
    <StyledMainContainer>
      <Header />
      <StyledHeaderWrapper>
        <StyledPageTitle variant="h4" component="h1" gutterBottom>
          Fabricantes
        </StyledPageTitle>
      </StyledHeaderWrapper>

      <StyledActionsBar>
        <TextField
          label="Filtrar por nombre, tecnología, estatus"
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        <Button
          variant="contained"
          sx={{ whiteSpace: 'nowrap', minWidth: '150px' }}
          onClick={handleCreateFabricanteClick}
        >
          Crear Fabricante
        </Button>
      </StyledActionsBar>

      <StyledTableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>Nombre</StyledTableHeaderCell>
              <StyledTableHeaderCell>Tecnología</StyledTableHeaderCell>
              <StyledTableHeaderCell>Estatus</StyledTableHeaderCell>
              <StyledTableHeaderCell>Plantas</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFabricantes.length > 0 ? (
              filteredFabricantes.map((fabricante) => (
                <TableRow key={fabricante.id}>
                  <TableCell
                    sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => handleEditClick(fabricante)}
                  >
                    {fabricante.nombre}
                  </TableCell>
                  <TableCell>{fabricante.tecnologia}</TableCell>
                  <TableCell>{fabricante.is_active ? 'Activo' : 'Inactivo'}</TableCell>
                  <TableCell>
                    {/*Usa el nuevo mapeo fabPltMap*/}
                    {(fabPltMap[fabricante.id] || [])
                      .map(planta => planta.nombre) // mapea a los nombres de las plantas
                      .join(', ')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledNoUsersCell colSpan={4}>No se encontraron fabricantes.</StyledNoUsersCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <FabricanteDetail
        open={openFabricanteDetail}
        onClose={handleCloseFabricanteDetail}
        fabricante={selectedFabricante}
        onSave={handleSaveFabricante}
      />
    </StyledMainContainer>
  );
}

export default FabricanteDashboard;
